<script>
    /**
     * Tab Bar
     *
     * @component TabBar
     * @example
     * <TabBarUI {tabs} bind:activeTabIndex on:tabchange} />
     *
     * @type {Array} tabs - [ { label , ...other-custom-data } ]
     * @type {number} activeTabIndex - specify the active index. default: 0
     * @event tabchange - event.detail => {tab, index} Called with the
     *        active tab data and the current index when the tab changes.
     */

    import { createEventDispatcher, onMount, onDestroy } from 'svelte';
    import Router, { pop, location } from 'svelte-spa-router';
    import { tick } from 'svelte';

    /**
     * Array of tab items
     * @type {Array}
     */
    export let tabs = [];

    /**
     * Index of the active tab
     * @type {Number}
     */
    export let activeTabIndex = 0;

    /**
     * Make tabs smaller with reduced font size
     * @type {Boolean}
     */
    export let small = false;

    /**
     * Array of objects with action detils
     * @type {Boolean}
     */

    const dispatch = createEventDispatcher();

    let tabRefs = [];
    let pillLeft = 0;
    let pillWidth = 0;

    async function updatePill() {
        await tick();
        if (tabRefs[activeTabIndex]) {
            const rect = tabRefs[activeTabIndex].getBoundingClientRect();
            const parentRect = tabRefs[activeTabIndex].parentElement.getBoundingClientRect();
            pillLeft = rect.left - parentRect.left;
            pillWidth = rect.width;
        }
    }

    function handleTabChange(newIndex) {
        if (newIndex === activeTabIndex) {
            dispatch('clickactive');
            // Do nothing if the index did not actually change
            return;
        }
        activeTabIndex = newIndex;
        dispatch('tabchange', { tab: tabs[activeTabIndex], index: activeTabIndex });
    }

    function updateTabIndex() {
        const index = tabs.findIndex((tab) => tab.route === $location);

        if (index != -1) {
            activeTabIndex = index;
        }
    }

    $: $location, updateTabIndex();
    $: activeTabIndex, tabs, updatePill();

</script>

<style lang="scss">
    .tabbar-outer {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        background: transparent;
    }

    .tabbar-pill {
        display: flex;
        align-items: center;
        border-radius: 999px;
        padding: 7px 6px;
        // min-height: 48px;
        width:100%;
        max-width:370px;
        position: relative;
        z-index: 2;
        overflow: visible;
        justify-content: space-between;
        background: rgba(0,0,0,0.1);
        box-shadow: inset 4px 4px 6px 0 rgba(0,0,0,0.2);
    }

    .active-pill {
        position: absolute;
        top: 6px;
        left: 0;
        height: 36px;
        background: #fff;
        box-shadow: 0 2px 8px 0 #0001;
        border-radius: 999px;
        z-index: 1;
        transition: left 0.32s cubic-bezier(.4,1,.4,1), width 0.32s cubic-bezier(.4,1,.4,1);
        will-change: left, width;
        pointer-events: none;
        background: var(--secondary-color);
        
        &.small {
            height: 32px;
            top: 8px;
        }
    }
    .tab {
        // color: white;
        padding: 0 22px;
        height: 36px;
        display: flex;
        align-items: center;
        border-radius: 999px;
        font-weight: 600;
        font-size: 1rem;
        background: transparent;
        position: relative;
        cursor: pointer;
        transition: color 200ms, background 200ms, box-shadow 200ms;
        z-index: 2;    
        &.active {
            color: var(--over-secondary-color);
        }
        
        &.small {
            font-size: 0.875rem; /* 14px */
            padding: 0 16px;
            height: 32px;
        }
    }
    
    .tabbar-track {
        width: 80%;
        height: 7px;
        background: linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 100%);
        border-radius: 999px;
        margin-top: -4px;
        box-shadow: 0 2px 8px 0 #0001;
        z-index: 1;
        position: relative;
    }
    .indicator {
        position: absolute;
        top: 2px;
        right: -0.625rem;
        width: 0.375rem;
        height: 0.375rem;
        border-radius: 3px;
        display: flex;
        justify-content: center;
        align-items: center;
        color: white;
        font-size: 0.687rem;
        font-weight: 600;
    }

</style>

<div class="tabbar-outer" style="width: {$$props.width || '100%'};">
    <div class="tabbar-pill {$$props.pillClass || ''}" class:small>
        <div class="active-pill" class:small style="left: {pillLeft}px; width: {pillWidth}px;"></div>
        {#each tabs as tab, index}
            {#if !tab.disabled}
                <div
                    class="tab {index === activeTabIndex ? 'active' : ''}"
                    class:active={index === activeTabIndex}
                    class:small
                    bind:this={tabRefs[index]}
                    on:click={() => handleTabChange(index)}
                >
                    {@html tab.label}
                    
                </div>
                {#if tab.showIndicator}
                    <span class="indicator" />
                {/if}
            {/if}
        {/each}
    </div>
</div>
